/**
 * Shared AI Conversation Context
 * Allows conversation state to persist between AIGuideHero and AICompanion
 * Now also persists to localStorage and backend for session recovery
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SESSION_KEY = 'coregulate_guide_session'; // Unified session key across all AI components
const SharedAIContext = createContext(null);

export const SharedAIProvider = ({ children }) => {
    const [messages, setMessages] = useState([]);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);

    // Initialize session and load history on mount
    useEffect(() => {
        const initSession = async () => {
            setIsLoadingHistory(true);
            try {
                let sid = localStorage.getItem(SESSION_KEY);
                
                if (!sid) {
                    sid = crypto.randomUUID();
                    localStorage.setItem(SESSION_KEY, sid);
                }
                
                setSessionId(sid);
                
                // Try to load history from backend
                const token = localStorage.getItem('co_regulate_token');
                const response = await fetch(`${API_URL}/api/guide/history/${sid}`, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.messages && data.messages.length > 0) {
                        setMessages(data.messages);
                        setHasInteracted(true);
                        console.log(`Companion: Restored ${data.messages.length} messages from session ${sid}`);
                    }
                }
            } catch (error) {
                console.error('Failed to initialize companion session:', error);
            } finally {
                setIsLoadingHistory(false);
            }
        };
        
        initSession();
    }, []);

    const addMessage = useCallback((message) => {
        setMessages(prev => [...prev, message]);
        setHasInteracted(true);
    }, []);

    const clearMessages = useCallback(async () => {
        // Clear on backend
        if (sessionId) {
            try {
                const token = localStorage.getItem('co_regulate_token');
                await fetch(`${API_URL}/api/guide/history/${sessionId}`, {
                    method: 'DELETE',
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });
            } catch (error) {
                console.error('Failed to clear companion history:', error);
            }
        }
        
        // Create new session
        const newSid = crypto.randomUUID();
        localStorage.setItem(SESSION_KEY, newSid);
        setSessionId(newSid);
        setMessages([]);
        setPendingNavigation(null);
        setHasInteracted(false);
    }, [sessionId]);

    const setNavigation = useCallback((nav) => {
        setPendingNavigation(nav);
    }, []);

    return (
        <SharedAIContext.Provider value={{
            messages,
            setMessages,
            addMessage,
            clearMessages,
            pendingNavigation,
            setNavigation,
            hasInteracted,
            setHasInteracted,
            sessionId,
            isLoadingHistory
        }}>
            {children}
        </SharedAIContext.Provider>
    );
};

export const useSharedAI = () => {
    const context = useContext(SharedAIContext);
    if (!context) {
        throw new Error('useSharedAI must be used within SharedAIProvider');
    }
    return context;
};

export default SharedAIContext;
